import { Clips, type ClipsListResponse, Collections, ShortUrl } from '@air/api';
import {
  type AssetSearchFilters,
  type Board,
  type BoardsListOptions,
  type Clip,
  type ClipsListOptions,
  ClipType,
  type ListResponse,
  type SortDirection,
  type SortField,
} from '@air/api/types';
import { callAirApi } from '@air/utils-api';
import { isAirrorInstance } from '@air/utils-error';

import { PageLimits } from '~/constants/PageLimits';

/**
 * The clip-type partition splitting one clip pool into the two gallery categories: every media
 * type feeds the assets galleries, nonMedia the files galleries. Exported so cache-surgery
 * helpers (insertItemsIntoCollectionGalleryQueries) route entities into the same caches the
 * fetchers below populate.
 */
// The `: ClipType[]` annotation matters: TypeScript infers a type predicate from the filter,
// which would narrow this to "every ClipType except nonMedia" — and then
// `ASSET_TYPES.includes(someClip.type)` wouldn't compile, because a plain ClipType isn't
// assignable to the narrowed element type. Declaring ClipType[] keeps `includes()` usable as the
// runtime "is this a media clip?" check the cache-insert helper performs.
export const ASSET_TYPES: ClipType[] = Object.values(ClipType).filter((type) => type !== ClipType.nonMedia);
export const FILE_TYPES = ClipType.nonMedia;

/**
 * Caller-supplied search params for the two share-link (`ShortUrl.Clips.search`) fetchers below,
 * derived from the endpoint's own options so it tracks future changes to them.
 *
 * Only `filters.collection` is subtracted. The endpoint types that one key as a three-way union —
 * `is` (a single collection id), `includeSubPages: true` (the whole non-draft sub-tree), or
 * neither (the root) — which the app-wide `AssetSearchFilters['collection']` is not assignable to,
 * since its `is` admits `string | string[]`. Subtracting the key rather than adopting the union
 * keeps the wider `Partial<ClipsListOptions>` assignable here, so the shared `ClipsFetcherParams`
 * and the private collection fetchers stay untouched.
 *
 * No caller supplies a collection filter — these fetchers own it, choosing the branch from
 * `expandNestedContent` and the sub-page id (see `getPublicShowcaseClipsFilters`).
 */
type PublicShowcaseClipsSearchParams = Omit<NonNullable<Parameters<typeof ShortUrl.Clips.search>[1]>, 'filters'> & {
  filters?: Omit<AssetSearchFilters, 'collection'>;
};

type PublicShowcaseClipsSearchFilters = NonNullable<
  NonNullable<Parameters<typeof ShortUrl.Clips.search>[1]>['filters']
>;

export type GetPublicShowcaseClipsFiltersParams = {
  /**
   * The active sub-page's own collection. Absent on the share link's root page, which is the
   * endpoint's default scope.
   */
  subPageCollectionId?: string;
  /**
   * Showcase root collection. Expand-nested on Home must send this as `collection.is` so
   * `scope: 'all'` is applied — `{ scope: 'all' }` without `is` is ignored and nested board
   * assets never load.
   */
  rootCollectionId: string;
  expandNestedContent: boolean;
  /**
   * An active search covers the WHOLE showcase — the root and every non-draft sub-page — wherever
   * the viewer is standing, so it takes the endpoint's `includeSubPages` branch and overrides the
   * page scoping below (FOUND-584).
   */
  isSearching: boolean;
  filters?: Omit<AssetSearchFilters, 'collection'>;
};

/**
 * Builds the `filters` the two share-link clip fetchers send, owning the `collection` key the
 * caller params deliberately can't set (see PublicShowcaseClipsSearchParams above).
 *
 * `scope: 'all'` only takes effect alongside `collection.is`, so every expanded listing sends the page's
 * own collection id (the root's on Home); without `is` the backend ignores `scope` and nested board
 * assets never load. Returning undefined (root page, not expanded) leaves the key off the request entirely so
 * the caller's `filters` flow through the spread untouched — that omitted-filter default is still
 * direct members only.
 */
export const getPublicShowcaseClipsFilters = ({
  subPageCollectionId,
  rootCollectionId,
  expandNestedContent,
  isSearching,
  filters,
}: GetPublicShowcaseClipsFiltersParams): PublicShowcaseClipsSearchFilters | undefined => {
  if (isSearching) {
    // Whole-tree search: the backend resolves the root + all non-draft sub-pages
    // (`includeSubPages`), and `scope: 'all'` reaches assets held by boards as well as direct
    // members — the "workspace-wide results" the search results page shows.
    return {
      ...filters,
      collection: { includeSubPages: true, scope: 'all' },
    };
  }

  if (subPageCollectionId) {
    return {
      ...filters,
      // `is` scopes to the sub-page's own collection; `scope: 'all'` additionally reaches assets
      // held by boards inside it, which is what an expanded view wants.
      collection: expandNestedContent ? { is: subPageCollectionId, scope: 'all' } : { is: subPageCollectionId },
    };
  }

  if (expandNestedContent) {
    return {
      ...filters,
      collection: { is: rootCollectionId, scope: 'all' },
    };
  }

  return undefined;
};

/**
 * Request-layer collection filter for workspace `Clips.search`. `includeSubPages` is the FOUND-866
 * flag (backend #8831): whole-tree expansion from `is`, not an id array. Installed `@air/api`
 * may not type it yet; extra keys still serialize, and `{ is: string }` stays assignable to
 * `SearchFilterCollection`.
 */
export type WorkspaceShowcaseCollectionFilter = NonNullable<AssetSearchFilters['collection']> & {
  includeSubPages?: true;
};

export type GetWorkspaceShowcaseClipsFiltersParams = {
  /** Showcase root — whole-tree search (`includeSubPages`) always expands from this id. */
  rootCollectionId: string;
  /**
   * The active sub-page's own collection. Absent on the root page, which scopes to
   * `rootCollectionId`.
   */
  subPageCollectionId?: string;
  expandNestedContent: boolean;
  /**
   * An active search covers the WHOLE showcase (FOUND-584 / FOUND-866), so it sends
   * `includeSubPages: true` on the root and ignores the page the author is standing on.
   */
  isSearching: boolean;
  filters?: Omit<AssetSearchFilters, 'collection'>;
};

export type WorkspaceShowcaseClipsFilters = Omit<AssetSearchFilters, 'collection'> & {
  collection: WorkspaceShowcaseCollectionFilter;
};

/**
 * Builds the `filters` workspace showcase clip/file fetchers send on `Clips.search`. Collection
 * is never inferred (there is no short id), so `collection.is` is always set.
 *
 * Browse listings always send `scope` explicitly. Workspace `Clips.search` defaults omitted scope
 * to `'all'` (board-held assets too), while the public ShortUrl listing's omitted-filter default is
 * direct members only — Edit/Preview would otherwise show nested board photos that the live `/a`
 * page hides.
 */
export const getWorkspaceShowcaseClipsFilters = ({
  rootCollectionId,
  subPageCollectionId,
  expandNestedContent,
  isSearching,
  filters,
}: GetWorkspaceShowcaseClipsFiltersParams): WorkspaceShowcaseClipsFilters => {
  if (isSearching) {
    return {
      ...filters,
      collection: { is: rootCollectionId, includeSubPages: true, scope: 'all' },
    };
  }

  // Flattened (expand-nested) listings reach board-held assets; the default listing block shows direct members only.
  const scope = expandNestedContent ? 'all' : 'direct';

  if (subPageCollectionId) {
    return {
      ...filters,
      collection: { is: subPageCollectionId, scope },
    };
  }

  return {
    ...filters,
    collection: { is: rootCollectionId, scope },
  };
};

type WorkspaceShowcaseBoardsFetcherParams = {
  cursor: string | null;
  collectionId: string;
  workspaceId: string;
  sortFieldName?: SortField['name'];
  sortDirection?: SortDirection;
  params: Partial<BoardsListOptions>;
  /**
   * A drilled board's id — lists that board's children inside the collection. Absent on a page
   * body, where the collection's own membership is listed (`parentBoardId` omitted).
   */
  parentBoardId?: string;
};

type WorkspaceShowcaseClipsFetcherParams = {
  cursor: string | null;
  /**
   * Collection the page is listing. On a sub-page this is that page's collection; on Home it is
   * the showcase root. Search overrides this via `rootCollectionId` + `includeSubPages`.
   */
  collectionId: string;
  workspaceId: string;
  sortFieldName: SortField['name'];
  sortDirection: SortDirection;
  params: Partial<ClipsListOptions>;
  expandNestedContent: boolean;
  /** Defaults to `collectionId` — pass the showcase root when listing a sub-page. */
  rootCollectionId?: string;
  /** The active sub-page's collection. Absent on Home and on the private collection page. */
  subPageCollectionId?: string;
  /** See `getWorkspaceShowcaseClipsFilters` — defaults to page-scoped listing. */
  isSearching?: boolean;
  /** A drilled board's id — scopes clips to that board inside the collection. */
  boardId?: string;
};

type PublicShowcaseClipsFetcherParams = {
  cursor: string | null;
  shortId: string;
  params: PublicShowcaseClipsSearchParams;
  sortFieldName: SortField['name'];
  sortDirection: SortDirection;
  expandNestedContent: boolean;
  /** See `getPublicShowcaseClipsFilters` — an active search takes the whole-tree branch. */
  isSearching: boolean;
  /** See `getPublicShowcaseClipsFilters` — absent on the share link's root page. */
  subPageCollectionId?: string;
  /** See `getPublicShowcaseClipsFilters` — the showcase root, sent as `collection.is` when expanded. */
  rootCollectionId: string;
};

type SearchPublicShowcaseClipsParams = PublicShowcaseClipsFetcherParams & {
  types: NonNullable<PublicShowcaseClipsSearchParams['types']>;
};

// Fetcher function for boards using ShortUrl.Boards.list
export const publicBoardsFetcher = async ({
  cursor,
  shortId,
  sortFieldName,
  sortDirection,
  params,
  subPageCollectionId,
}: {
  cursor: string | null;
  shortId: string;
  sortFieldName?: SortField['name'];
  sortDirection?: SortDirection;
  params: Partial<BoardsListOptions>;
  /**
   * The active sub-page's own collection — its boards are that collection's membership. Absent on
   * the share link's root page, where the endpoint defaults to the link's root collection.
   */
  subPageCollectionId?: string;
}): Promise<ListResponse<Board>> => {
  const result = await ShortUrl.Boards.list(shortId, {
    parentBoardId: undefined, // For collection root, don't filter by parent
    limit: !cursor ? PageLimits.subBoards.init : PageLimits.subBoards.more,
    cursor,
    ...params,
    collectionId: subPageCollectionId,
    sortField:
      sortFieldName && sortDirection
        ? {
            direction: sortDirection,
            name: sortFieldName,
          }
        : undefined,
    includeAncestors: true,
    numThumbnails: 1,
  });

  if (isAirrorInstance(result)) {
    throw result;
  }

  return result;
};

// Fetcher function for boards using Collections.Boards.list
export const privateBoardsFetcher = async ({
  cursor,
  collectionId,
  workspaceId,
  sortFieldName,
  sortDirection,
  params,
  parentBoardId,
}: WorkspaceShowcaseBoardsFetcherParams): Promise<ListResponse<Board>> =>
  callAirApi<ListResponse<Board>>(() =>
    Collections.Boards.list({
      workspaceId,
      collectionId,
      parentBoardId,
      limit: !cursor ? PageLimits.subBoards.init : PageLimits.subBoards.more,
      cursor,
      ...params,
      sortField:
        sortFieldName && sortDirection
          ? {
              direction: sortDirection,
              name: sortFieldName,
            }
          : undefined,
      includeAncestors: true,
      numThumbnails: 1,
    }),
  );

const searchPublicShowcaseClips = async ({
  cursor,
  shortId,
  params,
  sortFieldName,
  sortDirection,
  expandNestedContent,
  isSearching,
  subPageCollectionId,
  rootCollectionId,
  types,
}: SearchPublicShowcaseClipsParams): Promise<ClipsListResponse<Clip>> => {
  const filters = getPublicShowcaseClipsFilters({
    subPageCollectionId,
    rootCollectionId,
    expandNestedContent,
    isSearching,
    filters: params.filters,
  });

  return ShortUrl.Clips.search(shortId, {
    cursor,
    ...params,
    descendantBoardId: undefined, // For collection root, don't filter by descendant board
    types,
    limit: !cursor ? PageLimits.clips.init : PageLimits.clips.more,
    sortField: {
      direction: sortDirection,
      name: sortFieldName,
    },
    ...(filters && { filters }),
  });
};

// Fetcher function for clips using ShortUrl.Clips.search
export const publicClipsFetcher = (fetcherParams: PublicShowcaseClipsFetcherParams): Promise<ClipsListResponse<Clip>> =>
  searchPublicShowcaseClips({ ...fetcherParams, types: ASSET_TYPES });

// Fetcher function for clips using workspace Clips.search (FOUND-866 prefers this over the
// deprecated Collections.Assets.Versions.search). Collection scope is filters.collection.is.
export const privateClipsFetcher = async ({
  cursor,
  collectionId,
  workspaceId,
  sortFieldName,
  sortDirection,
  params,
  expandNestedContent,
  rootCollectionId,
  subPageCollectionId,
  isSearching = false,
  boardId,
}: WorkspaceShowcaseClipsFetcherParams): Promise<ClipsListResponse<Clip>> => {
  const { filters: callerFilters, ...restParams } = params;

  return Clips.search({
    workspaceId,
    ...restParams,
    boardId,
    cursor,
    types: ASSET_TYPES,
    search: params.search,
    limit: !cursor ? PageLimits.clips.init : PageLimits.clips.more,
    sortField: {
      direction: sortDirection,
      name: sortFieldName,
    },
    filters: getWorkspaceShowcaseClipsFilters({
      rootCollectionId: rootCollectionId ?? collectionId,
      subPageCollectionId,
      expandNestedContent,
      isSearching,
      filters: callerFilters,
    }),
  });
};

// Fetcher function for files using ShortUrl.Clips.search
export const publicFilesFetcher = (fetcherParams: PublicShowcaseClipsFetcherParams): Promise<ClipsListResponse<Clip>> =>
  searchPublicShowcaseClips({ ...fetcherParams, types: FILE_TYPES });

// Fetcher function for files using workspace Clips.search — same collection filters as clips,
// partitioned by nonMedia type.
export const privateFilesFetcher = async ({
  cursor,
  collectionId,
  workspaceId,
  sortFieldName,
  sortDirection,
  params,
  expandNestedContent,
  rootCollectionId,
  subPageCollectionId,
  isSearching = false,
  boardId,
}: WorkspaceShowcaseClipsFetcherParams): Promise<ClipsListResponse<Clip>> => {
  const { filters: callerFilters, ...restParams } = params;

  return Clips.search({
    workspaceId,
    ...restParams,
    boardId,
    cursor,
    types: FILE_TYPES,
    search: params.search,
    limit: !cursor ? PageLimits.clips.init : PageLimits.clips.more,
    sortField: {
      direction: sortDirection,
      name: sortFieldName,
    },
    filters: getWorkspaceShowcaseClipsFilters({
      rootCollectionId: rootCollectionId ?? collectionId,
      subPageCollectionId,
      expandNestedContent,
      isSearching,
      filters: callerFilters,
    }),
  });
};
